import { useState, useEffect, useCallback } from 'preact/hooks'
import { dropboxAuthApi } from '@/api/dropboxAuth'
import { apiClient } from '@/api/client'
import type { DropboxAuthStatusResult } from '@/api/dropboxAuth'

export function useDropboxAuth(enabled = true) {
    const [status, setStatus] = useState<DropboxAuthStatusResult | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isConnecting, setIsConnecting] = useState(false)

    const checkStatus = useCallback(async () => {
        setIsLoading(true)
        try {
            const response = await dropboxAuthApi.getStatus()
            if (response.data) {
                setStatus(response.data)
            } else {
                setStatus({ ProviderEmail: '', IsConnected: false })
            }
        } catch {
            setStatus({ ProviderEmail: '', IsConnected: false })
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        if (enabled) checkStatus()
    }, [enabled, checkStatus])

    const connect = useCallback(async () => {
        setIsConnecting(true)
        try {
            const response = await dropboxAuthApi.initiate()
            if (!response.data?.authorizationUrl) {
                throw new Error('Failed to get authorization URL')
            }

            const width = 600
            const height = 800
            const left = window.screenX + (window.outerWidth - width) / 2
            const top = window.screenY + (window.outerHeight - height) / 2
            const popup = window.open(
                response.data.authorizationUrl,
                'dropbox-oauth',
                `width=${width},height=${height},left=${left},top=${top},popup=yes`
            )

            if (!popup) {
                console.error('OAuth popup was blocked by the browser')
                setIsConnecting(false)
                return
            }

            const apiOrigin = new URL(apiClient.getBaseUrl()).origin
            const handleMessage = (event: MessageEvent) => {
                if (event.origin !== window.location.origin && event.origin !== apiOrigin) return
                if (event.source !== popup) return
                if (event.data?.type === 'dropbox-oauth-success') {
                    window.removeEventListener('message', handleMessage)
                    popup.close()
                    setIsConnecting(false)
                    checkStatus()
                }
            }
            window.addEventListener('message', handleMessage)

            const pollTimer = setInterval(() => {
                if (popup.closed) {
                    clearInterval(pollTimer)
                    window.removeEventListener('message', handleMessage)
                    setIsConnecting(false)
                    checkStatus()
                }
            }, 500)
        } catch (error) {
            console.error('Failed to initiate Dropbox OAuth:', error)
            setIsConnecting(false)
        }
    }, [checkStatus])

    const disconnect = useCallback(async () => {
        try {
            await dropboxAuthApi.disconnect()
            setStatus({ ProviderEmail: '', IsConnected: false })
        } catch (error) {
            console.error('Failed to disconnect Dropbox account:', error)
        }
    }, [])

    return {
        isConnected: status?.IsConnected ?? false,
        providerEmail: status?.ProviderEmail ?? '',
        isLoading,
        isConnecting,
        connect,
        disconnect,
        refreshStatus: checkStatus,
    }
}
